import { Alert, Button, Center, Group, Loader, Table, Text, useMantineTheme } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { routes } from "@/configs/router.config/routes";
import { useTags } from "@/hooks/useTags";
import { useWorkspaces } from "@/hooks/useWorkspaces";

const TAG_LIMIT = 20;

const truncateDescription = (description: string, maxLength: number = 100): string => {
  if (description.length <= maxLength) return description;
  return `${description.slice(0, maxLength)}...`;
};

const TagListPage: React.FC = () => {
  const { t } = useTranslation("admin");
  const theme = useMantineTheme();
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const { tags, isLoading } = useTags(sessionCurrentWorkspaceId);

  const tagCount = tags?.length ?? 0;
  const isLimitReached = tagCount >= TAG_LIMIT;

  const rows = tags?.map((tag, i) => (
    <Table.Tr key={tag.id}>
      <Table.Td>{i + 1}</Table.Td>
      <Table.Td>{tag.name}</Table.Td>
      <Table.Td title={tag.description}>{truncateDescription(tag.description)}</Table.Td>
      <Table.Td w={120}>
        <Group gap={10}>
          <Link to={routes.tagEdit(sessionCurrentWorkspaceId)(tag.id)} style={{ textDecoration: "none" }}>
            <Group gap={0}>
              <IconPencil size={16} color={theme.colors.blue[5]} />
              <Text c={theme.colors.blue[5]} size="sm">{t("tag.table.edit")}</Text>
            </Group>
          </Link>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Group justify="space-between">
        <h1>{t("tag.title")}</h1>
        <Link to={routes.tagCreate(sessionCurrentWorkspaceId)}>
          <Button disabled={isLimitReached}>{t("tag.createButton")}</Button>
        </Link>
      </Group>

      {isLimitReached && (
        <Alert color="yellow" mb="md">
          {t("tag.limitReachedMessage")}
        </Alert>
      )}

      <Text size="sm" c="dimmed" mb="md">
        {t("tag.tagCount", { count: tagCount, limit: TAG_LIMIT })}
      </Text>

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>#</Table.Th>
            <Table.Th>{t("tag.table.name")}</Table.Th>
            <Table.Th>{t("tag.table.description")}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        {!isLoading && <Table.Tbody>{rows}</Table.Tbody>}
      </Table>
      {isLoading && (
        <Center h={100}>
          <Loader color="gray" size="sm" data-testid="loader" />
        </Center>
      )}
      {!isLoading && (!tags || tags.length === 0) && (
        <Center h={100}>
          <Text c="gray">{t("tag.noTags")}</Text>
        </Center>
      )}
    </>
  );
};

export default TagListPage;
